import { Fragment } from 'react';
import { NavLink, useLocation } from 'react-router';
import { ChevronDown } from 'lucide-react';
import { Brand } from '@/design-system/components/brand';
import { Tooltip } from '@/design-system/components/tooltip';
import { useMe } from '@/lib/session';
import { cn } from '@/lib/utils';
import { SETTINGS_ITEM, visibleNav, type NavItem } from './nav';
import { UserMenu } from './user-menu';
import { ClientsNavigation } from './client-navigation';

function isActive(pathname: string, to: string) {
  return to === '/' || to === '/clients' ? pathname === to : pathname === to || pathname.startsWith(`${to}/`);
}

function Item({ item, compact, onNavigate }: { item: NavItem; compact: boolean; onNavigate?: () => void }) {
  const { pathname } = useLocation();
  const active = isActive(pathname, item.to);
  const Icon = item.icon;
  const link = (
    <NavLink
      to={item.children ? item.children[0]!.to : item.to}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'group relative flex h-9 items-center gap-3 rounded-control text-[13.5px] transition-colors',
        compact ? 'w-10 justify-center' : 'px-2.5',
        active ? 'bg-raised text-fg' : 'text-fg-muted hover:bg-hover hover:text-fg',
      )}
    >
      {/* Indicador de item ativo (ref. 05): filete no acento */}
      <span
        className={cn(
          'absolute top-1/2 -left-3 h-4 w-[3px] -translate-y-1/2 rounded-r-full bg-signal transition-opacity',
          active ? 'opacity-100' : 'opacity-0',
        )}
      />
      <Icon className={cn('size-[18px] shrink-0', active && 'text-fg')} strokeWidth={1.75} />
      {!compact && <span className="flex-1 truncate">{item.label}</span>}
      {!compact && item.children && (
        <ChevronDown
          className={cn('size-3.5 text-fg-subtle transition-transform', !active && '-rotate-90')}
        />
      )}
    </NavLink>
  );

  return (
    <li>
      {compact ? (
        <Tooltip label={item.label} side="right">
          {link}
        </Tooltip>
      ) : (
        link
      )}
      {!compact && active && item.children && (
        <ul className="mt-0.5 mb-1 ml-[21px] border-l border-line pl-3">
          {item.children.map((c) => (
            <li key={c.to}>
              <NavLink
                to={c.to}
                onClick={onNavigate}
                className={({ isActive: a }) =>
                  cn(
                    'flex h-8 items-center rounded-[8px] px-2.5 text-[13px] transition-colors',
                    a ? 'text-fg' : 'text-fg-subtle hover:text-fg',
                  )
                }
              >
                {c.label}
              </NavLink>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

/** Conteúdo da sidebar, usado na versão fixa (desktop), flutuante (oculta + peek) e drawer (mobile). */
export function SidebarContent({
  compact = false,
  onNavigate,
}: {
  compact?: boolean;
  onNavigate?: () => void;
}) {
  const me = useMe();
  const groups = visibleNav(me.permissions);
  return (
    <div className="flex h-full flex-col">
      <Brand compact={compact} />
      <nav
        aria-label="Principal"
        className={cn('flex-1 overflow-y-auto pt-2 pb-4', compact ? 'px-3.5' : 'px-3')}
      >
        {groups.map((g, i) => (
          <Fragment key={i}>
            <div className={cn(i > 0 && 'mt-5')}>
              {g.label && !compact && <p className="mb-1.5 px-2.5 text-[12px] text-fg-subtle">{g.label}</p>}
              {g.label && compact && <div className="mx-auto mb-3 h-px w-5 bg-line" />}
              <ul className="flex flex-col gap-0.5">
                {g.items.map((it) => (
                  <Item key={it.to} item={it} compact={compact} onNavigate={onNavigate} />
                ))}
              </ul>
            </div>
            {i === 0 && <ClientsNavigation compact={compact} onNavigate={onNavigate} />}
          </Fragment>
        ))}
      </nav>
      <div className={cn('flex flex-col gap-1 pb-3', compact ? 'items-center px-3.5' : 'px-3')}>
        <ul>
          <Item item={SETTINGS_ITEM} compact={compact} onNavigate={onNavigate} />
        </ul>
        <UserMenu compact={compact} />
      </div>
    </div>
  );
}
